import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import axiosInstance from 'config/axios';
import {
  DrugConcentration,
  GetDrugsWithConcentrationsRecord,
} from 'interfaces/drug-concentration';
import { ListConcentration } from 'interfaces/list-concentration';
import { ListDrug } from 'interfaces/list-drug';
import { Concentration } from 'interfaces/concentration';
import {
  PaginationResponse,
  PaginationResponseWithoutTotal,
  QueryCommonParams,
} from 'interfaces/pagination-response';
import { Drug } from 'interfaces/drug';
import { AxiosRequestConfig } from 'axios';

/**
 * @description List drug response type
 * @type {ListDrugResponse}
 */
export type ListDrugResponse = PaginationResponse<ListDrug>;

/**
 * @description Drug concentration response type
 * @type {DrugConcentrationResponse}
 */
export type DrugConcentrationResponse = {
  records: GetDrugsWithConcentrationsRecord[];
};

/**
 * @description Drug service
 * @type {DrugService}
 */
const DrugService = {
  /**
   * @description Create a new list drug in the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Partial<ListDrug>} data - List drug data
   *
   * @returns {Promise<{ record: ListDrug; totalRecords: ListDrug[] }>} List drug response with created record
   */
  create: (data: Partial<ListDrug>) => {
    return axiosInstance.post<{ record: ListDrug; totalRecords: ListDrug[] }>(
      '/v1/list-drugs',
      data,
    );
  },

  /**
   * @description Update an existing list drug in the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.4
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - List drug id
   * @param {Partial<ListDrug>} data - List drug data to update
   *
   * @returns {Promise<ListDrug>} Updated list drug response
   */
  update: (id: string | number, data: Partial<ListDrug>) => {
    return axiosInstance.patch<ListDrug>(`/v1/list-drugs/${id}`, data);
  },

  /**
   * @description Find a specific list drug by ID from the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - List drug id
   * @param {AxiosRequestConfig} config - Axios request config (optional)
   *
   * @returns {Promise<ListDrug>} List drug response
   */
  find: (id: string | number, config?: AxiosRequestConfig) => {
    return axiosInstance.get<ListDrug>(`/v1/list-drugs/${id}`, config);
  },

  /**
   * @description Delete a list drug from the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - List drug id
   *
   * @returns {Promise<ListDrug>} List drug response
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<ListDrug>(`/v1/list-drugs/${id}`);
  },

  /**
   * @description Get paginated list of drugs from the master catalog with sorting and filtering
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} queryParams - Query parameters including pagination, sorting, and filters
   *
   * @returns {Promise<ListDrugResponse & { totalConcentrations: number }>} List drug response with pagination
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
    includeBaseDrug?: boolean;
  }) => {
    return axiosInstance.get<
      ListDrugResponse & { totalConcentrations: number }
    >('/v1/list-drugs', {
      params: queryParams,
    });
  },

  /**
   * @description Get paginated list of drugs with their associated concentrations from the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} queryParams - Query parameters including pagination, sorting, and filters
   *
   * @returns {Promise<ListDrugResponse>} List drug response with concentrations
   */
  getListDrugsWithListConcentrations: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<ListDrugResponse>(
      '/v1/list-drugs/concentrations',
      {
        params: queryParams,
      },
    );
  },

  /**
   * @description Get paginated list of concentrations for a specific drug from the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {number | string} drugId - Drug id
   * @param {QueryCommonParams} queryParams - Query parameters
   *
   * @returns {Promise<PaginationResponseWithoutTotal<ListConcentration>>} List concentration response
   */
  getListConcentrations: (
    drugId: number | string,
    queryParams: QueryCommonParams,
  ) => {
    return axiosInstance.get<PaginationResponseWithoutTotal<ListConcentration>>(
      `/v1/list-drugs/${drugId}/list-concentrations`,
      {
        params: queryParams,
      },
    );
  },

  /**
   * @description Delete a specific concentration from a list drug in the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} drugId - Drug id
   * @param {string | number} id - List concentration id
   *
   * @returns {Promise<ListConcentration>} List concentration response
   */
  deleteListConcentration: (drugId: string | number, id: string | number) => {
    return axiosInstance.delete<ListConcentration>(
      `/v1/list-drugs/${drugId}/list-concentrations/${id}`,
    );
  },

  /**
   * @description Create a new concentration for a list drug in the master catalog
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} drugId - Drug id
   * @param {Partial<ListConcentration>} data - List concentration data
   *
   * @returns {Promise<ListConcentration>} List concentration response
   */
  createConcentration: (
    drugId: string | number,
    data: Partial<ListConcentration>,
  ) => {
    return axiosInstance.post<ListConcentration>(
      `/v1/list-drugs/${drugId}/list-concentrations`,
      data,
    );
  },

  /**
   * @description Assign a concentration from the master catalog to a specific care area in a facility
   *
   * @SADtrace SAD-003-4.2, SAD-003-4.17
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} data - Assignment data containing care area, facility, drug, and concentration IDs
   *
   * @returns {Promise<DrugConcentration>} Drug concentration response
   */
  assignConcentrationToCareArea(data: {
    careAreaId: number;
    facilityId: number;
    listDrugId: number;
    listConcentrationId: number;
  }) {
    return axiosInstance.post<DrugConcentration>(
      `/v1/drugs/concentrations`,
      data,
    );
  },

  /**
   * @description Get drugs with their concentrations for a specific care area and facility
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {QueryCommonParams & { careAreaId: number; facilityId: number }} queryParams - Query parameters with care area and facility IDs
   *
   * @returns {Promise<PaginationResponseWithoutTotal<GetDrugsWithConcentrationsRecord>>} Drugs with concentrations response
   */
  getDrugsWithConcentrations: (
    queryParams: QueryCommonParams & {
      careAreaId: number;
      facilityId: number;
    },
  ) => {
    return axiosInstance.get<
      PaginationResponseWithoutTotal<GetDrugsWithConcentrationsRecord>
    >('/v1/drugs/concentrations', {
      params: queryParams,
    });
  },

  /**
   * @description Delete a concentration from a care area drug library
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.20
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Concentration id
   * @param {Object} queryParams - Query parameters with care area and facility IDs
   *
   * @returns {Promise<void>} Void response
   */
  deleteConcentration: (
    id: string | number,
    queryParams: {
      careAreaId: number;
      facilityId: number;
    },
  ) => {
    return axiosInstance.delete(`/v1/drugs/concentrations/${id}`, {
      params: queryParams,
    });
  },

  /**
   * @description Delete a drug from a care area drug library
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.20
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Drug id
   * @param {Object} queryParams - Query parameters with care area and facility IDs
   *
   * @returns {Promise<void>} Void response
   */
  deleteDrug: (
    id: string | number,
    queryParams: {
      careAreaId: number;
      facilityId: number;
    },
  ) => {
    return axiosInstance.delete(`/v1/drugs/${id}`, {
      params: queryParams,
    });
  },

  /**
   * @description Get a specific drug by ID from a care area drug library
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Drug id
   *
   * @returns {Promise<Drug>} Drug response
   */
  getDrug: (id: string | number) => {
    return axiosInstance.get<Drug>(`/v1/drugs/${id}`);
  },

  /**
   * @description Get a specific concentration by ID from a care area drug library
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Concentration id
   *
   * @returns {Promise<Concentration>} Concentration response
   */
  getConcentration: (id: string | number) => {
    return axiosInstance.get(`/v1/drugs/concentrations/${id}`);
  },

  /**
   * @description Update an existing concentration in a care area drug library
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3, SAD-003-4.4
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Concentration id
   * @param {Partial<Concentration>} data - Concentration data to update
   *
   * @returns {Promise<Concentration>} Concentration response
   */
  updateConcentration: (id: string | number, data: Partial<Concentration>) => {
    return axiosInstance.patch(`/v1/drugs/concentrations/${id}`, data);
  },

  /**
   * @description Delete an advisory assignment from a concentration
   *
   * @SADtrace SAD-003-4.18
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Concentration id
   *
   * @returns {Promise<void>} Void response
   */
  deleteAdvisory: (id: string | number) => {
    return axiosInstance.delete(`/v1/drugs/concentrations/${id}/advisories`);
  },

  /**
   * @description Assign an advisory to a concentration
   *
   * @SADtrace SAD-003-4.18
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - Concentration id
   * @param {Object} data - Advisory assignment data containing advisory_id
   *
   * @returns {Promise<void>} Void response
   */
  assignAdvisory: (
    id: string | number,
    data: {
      advisory_id: string | number;
    },
  ) => {
    return axiosInstance.patch(
      `/v1/drugs/concentrations/${id}/advisories`,
      data,
    );
  },

  /**
   * @description Copy a drug with all its concentrations to another care area
   *
   * @SADtrace SAD-003-4.19
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} drugId - Drug id to copy
   * @param {string | number} careAreaId - Target care area id
   * @param {string | number} facilityId - Target facility id
   *
   * @returns {Promise<void>} Void response
   */
  copyDrugToCareArea: (
    drugId: string | number,
    careAreaId: string | number,
    facilityId: string | number,
  ) => {
    return axiosInstance.post(`/v1/drugs/${drugId}/copy`, {
      care_area_id: +careAreaId,
      facility_id: +facilityId,
    });
  },
};

export default DrugService;
